import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Menu,
  InputAdornment,
  Grid,
} from "@mui/material";

import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Search as SearchIcon,
  LocationOn as LocationOnIcon,
} from "@mui/icons-material";

import { useUnitStore } from "../stores/unit.store";
import { UnitSchema, UnitRequest, UpdateUnitRequest } from "../types/unit.type";

type DialogMode = "add" | "edit";

const Units: React.FC = () => {
  const {
    getAllUnits,
    createNewUnit,
    updateUnitById,
    removeUnit,
    isLoading,
    error,
  } = useUnitStore();

  const [units, setUnits] = useState<UnitSchema[]>([]);
  const [loading, setLoading] = useState(false);

  // Search state
  const [searchQuery, setSearchQuery] = useState("");

  const [open, setOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<DialogMode>("add");
  const [selectedUnit, setSelectedUnit] = useState<UnitSchema | null>(null);

  const [formData, setFormData] = useState<{
    name: string;
    latitude: string;
    longitude: string;
    location_description: string;
    price: string;
    google_maps_url: string;
    image_url: string;
    pod_type: string;
    secret: string;
    lock_id: string;
  }>({
    name: "",
    latitude: "",
    longitude: "",
    location_description: "",
    price: "",
    google_maps_url: "",
    image_url: "",
    pod_type: "",
    secret: "",
    lock_id: "",
  });

  // Actions menu state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuUnit, setMenuUnit] = useState<UnitSchema | null>(null);

  /* --------------------------------- Loaders -------------------------------- */

  useEffect(() => {
    loadUnits();
  }, []);

  const loadUnits = async () => {
    setLoading(true);
    try {
      const allUnits = await getAllUnits();
      setUnits(allUnits || []);
    } catch (err) {
      console.error("Error fetching units", err);
    } finally {
      setLoading(false);
    }
  };

  /* -------------------------- Filtered Units -------------------------- */

  const filteredUnits = useMemo(() => {
    let result = [...units];

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (u) =>
          (u.name || "").toLowerCase().includes(query) ||
          (u.location_description || "").toLowerCase().includes(query) ||
          (u.pod_type || "").toLowerCase().includes(query)
      );
    }

    return result;
  }, [units, searchQuery]);

  /* ------------------------------ Add / Edit ------------------------------- */

  const handleOpen = (mode: DialogMode, unit?: UnitSchema) => {
    setDialogMode(mode);

    if (mode === "edit" && unit) {
      setSelectedUnit(unit);
      setFormData({
        name: unit.name || "",
        latitude: String(unit.latitude || ""),
        longitude: String(unit.longitude || ""),
        location_description: unit.location_description || "",
        price: String(unit.price || ""),
        google_maps_url: unit.google_maps_url || "",
        image_url: unit.image_url || "",
        pod_type: unit.pod_type || "",
        secret: "",
        lock_id: unit.lock_id ? String(unit.lock_id) : "",
      });
    } else {
      setSelectedUnit(null);
      setFormData({
        name: "",
        latitude: "",
        longitude: "",
        location_description: "",
        price: "",
        google_maps_url: "",
        image_url: "",
        pod_type: "",
        secret: "",
        lock_id: "",
      });
    }
    setOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.name.trim()) {
      alert("Please enter a name");
      return;
    }
    if (!formData.latitude.trim()) {
      alert("Please enter latitude");
      return;
    }
    if (!formData.longitude.trim()) {
      alert("Please enter longitude");
      return;
    }
    if (!formData.location_description.trim()) {
      alert("Please enter location description");
      return;
    }
    if (!formData.price.trim()) {
      alert("Please enter price");
      return;
    }
    if (!formData.google_maps_url.trim()) {
      alert("Please enter Google Maps URL");
      return;
    }
    if (!formData.image_url.trim()) {
      alert("Please enter image URL");
      return;
    }
    if (!formData.pod_type.trim()) {
      alert("Please enter pod type");
      return;
    }

    if (dialogMode === "add") {
      const unitBody: UnitRequest = {
        name: formData.name,
        latitude: parseFloat(formData.latitude),
        longitude: parseFloat(formData.longitude),
        location_description: formData.location_description,
        price: parseFloat(formData.price),
        google_maps_url: formData.google_maps_url,
        image_url: formData.image_url,
        pod_type: formData.pod_type,
        secret: formData.secret || null,
        lock_id: formData.lock_id ? parseInt(formData.lock_id) : null,
        data: {},
      };

      await createNewUnit(unitBody);
    } else if (dialogMode === "edit") {
      const updateBody: UpdateUnitRequest = {
        name: formData.name,
        latitude: parseFloat(formData.latitude),
        longitude: parseFloat(formData.longitude),
        location_description: formData.location_description,
        price: parseFloat(formData.price),
        google_maps_url: formData.google_maps_url,
        image_url: formData.image_url,
        pod_type: formData.pod_type,
        secret: formData.secret || null,
        lock_id: formData.lock_id ? parseInt(formData.lock_id) : null,
        data: {},
      };

      await updateUnitById(selectedUnit!.id, updateBody);
    }

    setOpen(false);
    loadUnits();
  };

  /* ------------------------------ Actions Menu -------------------------- */

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    unit: UnitSchema
  ) => {
    setAnchorEl(event.currentTarget);
    setMenuUnit(unit);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuUnit(null);
  };

  const handleEditUnit = () => {
    if (menuUnit) {
      handleOpen("edit", menuUnit);
    }
    handleMenuClose();
  };

  const handleDeleteUnit = async () => {
    if (!menuUnit) return;

    if (window.confirm("Are you sure you want to delete this unit?")) {
      await removeUnit(menuUnit.id);
      loadUnits();
    }
    handleMenuClose();
  };

  /* --------------------------------- Render -------------------------------- */

  if ((loading || isLoading) && units.length === 0) {
    return (
      <Box display="flex" justifyContent="center" mt={6}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" mb={3}>
        <Typography variant="h4">Units</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpen("add")}
        >
          Add Unit
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Search Section */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by name, location, or pod type..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>

        {searchQuery && (
          <Box mt={2}>
            <Typography variant="body2" color="text.secondary">
              Showing {filteredUnits.length} of {units.length} units
              {searchQuery && ` matching "${searchQuery}"`}
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Units Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <strong>Name</strong>
              </TableCell>
              <TableCell>
                <strong>Location</strong>
              </TableCell>
              <TableCell>
                <strong>Pod Type</strong>
              </TableCell>
              <TableCell>
                <strong>Price</strong>
              </TableCell>
              <TableCell>
                <strong>Lock ID</strong>
              </TableCell>
              <TableCell align="right">
                <strong>Actions</strong>
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {filteredUnits.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ py: 3 }}
                  >
                    {searchQuery
                      ? "No units found matching your search"
                      : "No units found"}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredUnits.map((u) => {
                return (
                  <TableRow key={u.id} hover>
                    <TableCell>{u.name}</TableCell>
                    <TableCell>{u.location_description}</TableCell>
                    <TableCell>{u.pod_type}</TableCell>
                    <TableCell>{u.price}</TableCell>
                    <TableCell>{u.lock_id || "N/A"}</TableCell>
                    <TableCell align="right">
                      <Box display="flex" gap={0.5} justifyContent="flex-end">
                        {u.google_maps_url && (
                          <IconButton
                            size="small"
                            onClick={() => window.open(u.google_maps_url, "_blank")}
                            title="Open in Google Maps"
                            color="primary"
                          >
                            <LocationOnIcon />
                          </IconButton>
                        )}
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, u)}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEditUnit}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Edit Unit
        </MenuItem>
        <MenuItem onClick={handleDeleteUnit} sx={{ color: "error.main" }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete Unit
        </MenuItem>
      </Menu>

      {/* Add / Edit Unit Dialog */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {dialogMode === "add" && "Add Unit"}
          {dialogMode === "edit" && "Edit Unit"}
        </DialogTitle>

        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Name"
                  required
                  value={formData.name}
                  onChange={(e) =>
                    setFormData((p) => ({ ...p, name: e.target.value }))
                  }
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Latitude"
                  type="number"
                  required
                  value={formData.latitude}
                  onChange={(e) =>
                    setFormData((p) => ({ ...p, latitude: e.target.value }))
                  }
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Longitude"
                  type="number"
                  required
                  value={formData.longitude}
                  onChange={(e) =>
                    setFormData((p) => ({ ...p, longitude: e.target.value }))
                  }
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Location Description"
                  required
                  multiline
                  rows={2}
                  value={formData.location_description}
                  onChange={(e) =>
                    setFormData((p) => ({
                      ...p,
                      location_description: e.target.value,
                    }))
                  }
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Price"
                  type="number"
                  required
                  value={formData.price}
                  onChange={(e) =>
                    setFormData((p) => ({ ...p, price: e.target.value }))
                  }
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Pod Type"
                  required
                  value={formData.pod_type}
                  onChange={(e) =>
                    setFormData((p) => ({ ...p, pod_type: e.target.value }))
                  }
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Google Maps URL"
                  required
                  value={formData.google_maps_url}
                  onChange={(e) =>
                    setFormData((p) => ({
                      ...p,
                      google_maps_url: e.target.value,
                    }))
                  }
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Image URL"
                  required
                  value={formData.image_url}
                  onChange={(e) =>
                    setFormData((p) => ({ ...p, image_url: e.target.value }))
                  }
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Secret (Optional)"
                  value={formData.secret}
                  onChange={(e) =>
                    setFormData((p) => ({ ...p, secret: e.target.value }))
                  }
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Lock ID (Optional)"
                  type="number"
                  value={formData.lock_id}
                  onChange={(e) =>
                    setFormData((p) => ({ ...p, lock_id: e.target.value }))
                  }
                />
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions>
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" variant="contained">
              {dialogMode === "add" && "Create"}
              {dialogMode === "edit" && "Update"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default Units;
